import React, { useEffect } from "react";
import { IoChevronBack, IoHomeOutline } from "react-icons/io5";

/**
 * Cursor-style back/home control for the full-page settings shell.
 * Escape triggers the same dismiss path as clicking the button, unless a nested
 * modal dialog or settings info tip is open (those own Escape first).
 *
 * @example
 * <BusinessAppSettingsCloseButton onClick={() => navigate("/business/action-items")} />
 *
 * @param {object} props
 * @param {() => void} props.onClick - Navigate back to the main tasks page
 * @param {string} [props.ariaLabel="Back to tasks"]
 */
const BusinessAppSettingsCloseButton = ({
  onClick,
  ariaLabel = "Back to tasks",
  extraStyle,
}) => {
  useEffect(() => {
    if (typeof onClick !== "function") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      // Nested overlays should dismiss before leaving settings.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
        return;
      }
      if (document.querySelector(".settings-info-tip__popover")) {
        return;
      }
      onClick();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClick]);

  return (
    <button
      type="button"
      className="business-app-settings-back-home"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      style={extraStyle}
    >
      <IoChevronBack
        className="business-app-settings-back-home__back"
        aria-hidden="true"
      />
      <IoHomeOutline
        className="business-app-settings-back-home__home"
        aria-hidden="true"
      />
    </button>
  );
};

export default BusinessAppSettingsCloseButton;
